import {
  Box,
  ColumnLayout,
  Link,
} from '@cloudscape-design/components';
import useInventoryByProductDocId from 'features/inventory/hooks/useInventoryByProductDocId';
import ProductDisplay from 'features/products/components/ProductDisplay';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, {
  useState,
} from 'react';

import ProgressCircle from './progressCircle';
import ProjectHeader from './projectHeader';
import ShippingDate from './shippingDate';

function InventorySection({ productDocId, productVersionId }) {
  const { data: inventory = [] } = useInventoryByProductDocId(productDocId);

  const orderedVersion = inventory.find((i) => i.productVersionId === productVersionId);
  const otherVersions = inventory.filter(
    (i) => i.productVersionId !== productVersionId && (i.onHand ?? 0) > 0,
  );

  return (
    <>
      <div>
        <Box variant="awsui-key-label">
          On Hand
        </Box>
        <Link href={`/inventory/${productDocId}/${productVersionId}`}>
          {orderedVersion?.onHand ?? 0}
        </Link>
      </div>
      {otherVersions.map((inv) => (
        <div key={inv.productVersionId}>
          <Box variant="awsui-key-label">
            {`On Hand (${inv.productVersionId})`}
          </Box>
          <Link href={`/inventory/${productDocId}/${inv.productVersionId}`}>
            {inv.onHand}
          </Link>
        </div>
      ))}
    </>
  );
}

InventorySection.propTypes = {
  productDocId: PropTypes.string.isRequired,
  productVersionId: PropTypes.string.isRequired,
};

export default function ProductContainer({ product }) {
  const {
    orderId,
    orgId,
    lineItemId,
    notes,
    productDocId,
    productVersionId,
    projectId,
    pPartId,
    required,
    shipped,
    targetShipDate,
    shipDateActual,
    shipDateBusinessDays,
  } = product;

  const { user: { canEditOrder } } = useUserData();
  const [expanded, setExpanded] = useState(true);

  return (
    <li className={`tracker-project${expanded ? ' expanded' : ''}`}>
      <ProjectHeader
        expanded={expanded}
        toggleView={() => setExpanded((prev) => !prev)}
        projectId={<ProductDisplay productDocId={productDocId} fields={['sku', 'name']} />}
        orgId={orgId}
      />
      {expanded ? (
        <div className="expandedView">
          <div className="finalListItems">
            <ul>
              <li className={canEditOrder ? 'withLink' : undefined}>
                <div className="badgeContainer">
                  <p className="badge finalLineItem">
                    {[projectId, pPartId].filter(Boolean).join('-')}
                  </p>
                  {canEditOrder ? (
                    <Link
                      color="inverted"
                      variant="info"
                      external
                      href={`/orders/update/${orderId}#item_${lineItemId.split('_').slice(-2).join('_')}`}
                    />
                  ) : null}
                </div>
                <p className={`notes${notes?.length <= 0 ? ' empty' : ''}`}>
                  {notes?.length <= 0 ? 'No notes.' : notes}
                </p>
                <ShippingDate
                  numParts={shipped || required}
                  shipDateTarget={targetShipDate}
                  shipDateActual={shipDateActual}
                  shipDateBusinessDays={shipDateBusinessDays}
                />
              </li>
            </ul>
            <Box margin={{ top: 'm' }}>
              <ColumnLayout columns={4} variant="text-grid">
                {productDocId && (
                  <div>
                    <Box variant="awsui-key-label">Product / Version</Box>
                    <Link href={`/products/${productDocId}`} external>
                      <ProductDisplay productDocId={productDocId} fields={['sku', 'name']} />
                      {` / ${productVersionId || '-'}`}
                    </Link>
                  </div>
                )}
                <div>
                  <Box variant="awsui-key-label">Required</Box>
                  <div>{required}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Shipped</Box>
                  <div>{shipped}</div>
                </div>
                <InventorySection
                  productDocId={productDocId}
                  productVersionId={productVersionId}
                />
              </ColumnLayout>
            </Box>
          </div>
        </div>
      ) : (
        <ul className="collapsedListView">
          <li className="shipment">
            <ShippingDate
              shipDateTarget={targetShipDate}
              shipDateActual={shipDateActual}
              shipDateBusinessDays={shipDateBusinessDays}
            />
            <ul className="lineItems">
              <li>
                <button
                  className="lineItem"
                  type="button"
                  onClick={() => setExpanded(true)}
                >
                  <span className="badge finalLineItem">
                    {[projectId, pPartId].filter(Boolean).join('-')}
                  </span>
                  <span className="progress">
                    <span className="passed">
                      {shipped}
                    </span>
                    /
                    {required}
                  </span>
                  <ProgressCircle
                    passed={shipped}
                    goal={required}
                  />
                </button>
              </li>
            </ul>
          </li>
        </ul>
      )}
    </li>
  );
}

ProductContainer.propTypes = {
  product: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    lineItemId: PropTypes.string.isRequired,
    orgId: PropTypes.string.isRequired,
    productDocId: PropTypes.string,
    productVersionId: PropTypes.string,
    projectId: PropTypes.string,
    pPartId: PropTypes.string,
    notes: PropTypes.string,
    required: PropTypes.number.isRequired,
    shipped: PropTypes.number.isRequired,
    targetShipDate: PropTypes.string,
    shipDateActual: PropTypes.string,
    shipDateBusinessDays: PropTypes.number,
  }).isRequired,
};
